import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { CourseCommentsResponse } from '@lib/interfaces/course-comments.interface';
import { CourseCard, CourseStatusCounts, MyCourse } from '@lib/interfaces/course.interface';
import { Observable, shareReplay } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CourseService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;
    private readonly programTypesCache = new Map<boolean, Observable<any>>();
    private readonly topicsWithCountCache = new Map<string, Observable<any>>();
    uploadMedia(file: File, type: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        return this.http.post(`${this.baseUrl}/media/upload`, formData);
    }

    getDepartments(): Observable<any> {
        return this.http.get(`${this.baseUrl}/department`);
    }

    getPrograms(): Observable<any> {
        return this.http.get(`${this.baseUrl}/program`);
    }

    getProgramTypesWithParent(isParent: boolean = false): Observable<any> {
        if (!this.programTypesCache.has(isParent)) {
            const params = new HttpParams().set('isParent', isParent.toString());
            this.programTypesCache.set(
                isParent,
                this.http.get(`${this.baseUrl}/program`, { params }).pipe(shareReplay(1)),
            );
        }
        return this.programTypesCache.get(isParent)!;
    }

    getLocations(): Observable<{ data: any[]; totalRecords: number; errors: any }> {
        return this.http.get<{ data: any[]; totalRecords: number; errors: any }>(`${this.baseUrl}/location`);
    }

    getCourseTypes(): Observable<any> {
        return this.http.get(`${this.baseUrl}/enrollment-type`);
    }

    getTopics(searchParam?: string): Observable<any> {
        let params = new HttpParams();
        if (searchParam) {
            params = params.set('searchParam', searchParam);
        }
        return this.http.get(`${this.baseUrl}/topic`, { params });
    }

    getTopicsWithCount(
        search?: string,
        page: number = 1,
        size: number = 10,
        isParent: boolean = false,
    ): Observable<any> {
        const cacheKey = `${search ?? ''}|${page}|${size}|${isParent}`;
        if (!this.topicsWithCountCache.has(cacheKey)) {
            let params = new HttpParams()
                .set('page', page.toString())
                .set('size', size.toString())
                .set('isParent', isParent.toString());

            if (search && search.length >= 3) {
                params = params.set('search', search);
            }

            this.topicsWithCountCache.set(
                cacheKey,
                this.http.get(`${this.baseUrl}/students/topics/count`, { params }).pipe(shareReplay(1)),
            );
        }
        return this.topicsWithCountCache.get(cacheKey)!;
    }

    getEnrollmentTypes(): Observable<any> {
        return this.http.get(`${this.baseUrl}/enrollment-type`);
    }

    getCoFaculty(): Observable<any> {
        return this.http.get(`${this.baseUrl}/user`, {
            params: new HttpParams().set('role', 'FACULTY'),
        });
    }

    getSources(): Observable<any> {
        return this.http.get(`${this.baseUrl}/source`);
    }

    getCoursePreview(courseId: number | string): Observable<any> {
        return this.http.get(`${this.baseUrl}/course/preview/${courseId}`);
    }

    getCoursesByStatus(status: string, searchParams?: string): Observable<any> {
        let params = new HttpParams();
        if (status) {
            params = params.set('status', status);
        }
        if (searchParams) {
            params = params.set('search', searchParams);
        }
        return this.http.get<MyCourse[]>(`${this.baseUrl}/course/courses/get-Course`, { params });
    }

    getCoursesByMultipleStatuses(options: {
        statuses: string[];
        searchParams?: string;
        isFeedbackForm?: boolean;
    }): Observable<any> {
        let params = new HttpParams();

        // Add each status as a separate parameter with the same key
        if (options.statuses?.length > 0) {
            options.statuses.forEach((status) => {
                params = params.append('status', status);
            });
        }

        if (options.searchParams) {
            params = params.set('search', options.searchParams);
        }

        if (options.isFeedbackForm) {
            params = params.set('isFeedbackForm', 'true');
        }

        return this.http.get<MyCourse[]>(`${this.baseUrl}/course/courses/get-Course`, { params });
    }

    downloadFileByUrl(fileUrl: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/file-system/${fileUrl}`, {
            responseType: 'blob',
        });
    }

    getCourseStatusCounts(): Observable<CourseStatusCounts> {
        return this.http.get<CourseStatusCounts>(`${this.baseUrl}/course/status-counts`);
    }

    getCourseProposal(courseId: number | string): Observable<any> {
        return this.http.get(`${this.baseUrl}/proposals/course/${courseId}`);
    }

    getCourseComments(courseId: number | string): Observable<CourseCommentsResponse> {
        return this.http.get<CourseCommentsResponse>(`${this.baseUrl}/course/comments/${courseId}`);
    }

    submitStudentApplication(formData: FormData): Observable<any> {
        return this.http.post(`${this.baseUrl}/student/application`, formData);
    }

    getPopularCourses(): Observable<{ data: CourseCard[] }> {
        return this.http.get<{ data: CourseCard[] }>(`${this.baseUrl}/students/course/popular`);
    }

    getYoutubeVideoMetaData(videoUrl: string): Observable<any> {
        const videoId = this.extractVideoId(videoUrl);
        if (!videoId) {
            throw new Error('Invalid YouTube URL format');
        }
        return this.http.get(
            `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`,
        );
    }

    private extractVideoId(url: string): string | null {
        const patterns = [
            /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([^?&/]+)/,
            /youtube\.com\/watch\?v=([^&]+)/,
            /youtu\.be\/([^?]+)/,
            /youtube\.com\/embed\/([^?]+)/,
            /youtube\.com\/v\/([^?]+)/,
        ];

        for (const pattern of patterns) {
            const match = pattern.exec(url);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    getCourseDetails(courseId: string): Observable<any> {
        return this.http.get(`${this.baseUrl}/students/course-details`, { params: { courseId } });
    }

    getRelatedCourses(
        facultyId: number,
        page: number = 1,
        size: number = 10,
        isParent: boolean = false,
    ): Observable<{ data: CourseCard[]; hasNext: boolean }> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString())
            .set('isParent', isParent.toString())
            .set('facultyId', facultyId.toString());

        return this.http.get<{ data: CourseCard[]; hasNext: boolean }>(`${this.baseUrl}/students/course/popular`, {
            params,
        });
    }
}
